// "Recent capture" — a flat, newest-first stream of what memory just recorded: each card is one
// captured takeaway plus the question it answers. Deliberately NOT the topic tree — a lightweight
// "what just happened" glance. Presented as a live TIMELINE: a teal rail descends from the "Live"
// head, with a node dot per capture and an always-visible card to its right. Clicking a card opens
// the Memory-log tree landed (oriented) on that node. Shared styling comes from memoryTheme so this
// tab and Impact read as one system. Data: GET /me/recent-captures (newest-first items).
import { Box, CircularProgress, Typography } from "@mui/material";
import { keyframes } from "@mui/system";
import { C, CARD, CARD_LEAD, MICRO_LABEL, SectionHeader, TimeChip } from "./memoryTheme";

const beat = keyframes`
  0%   { box-shadow: 0 0 0 0 rgba(45,168,148,.45); }
  70%  { box-shadow: 0 0 0 7px rgba(45,168,148,0); }
  100% { box-shadow: 0 0 0 0 rgba(45,168,148,0); }
`;

function relTime(sec) {
  if (!sec) return "";
  const s = Math.max(0, Math.round(Date.now() / 1000 - sec));
  if (s < 60) return "just now";
  const m = Math.round(s / 60);
  if (m < 60) return `${m}m ago`;
  const h = Math.round(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.round(h / 24);
  return d === 1 ? "yesterday" : `${d}d ago`;
}

// A capture as a timeline node: dot on the rail (left), always-visible card (right). The card is
// clickable — it opens the Memory-log tree landed (oriented) on this node. The "Organized under"
// mini-path previews that placement in-card.
function CaptureRow({ item, onOpen }) {
  return (
    <Box sx={{ position: "relative", pl: "30px", mb: 1.5 }}>
      {/* node dot — sits on the rail (same 8px as the Live head), masked from the line by a ring */}
      <Box component="span" sx={{ position: "absolute", left: "3px", top: "21px", width: 8, height: 8,
             borderRadius: "50%", bgcolor: C.teal, boxShadow: `0 0 0 3px ${C.canvas}`, zIndex: 1 }} />
      <Box onClick={() => onOpen && onOpen(item.id)}
           sx={{ ...CARD, p: 2, cursor: "pointer",
                 transition: "border-color .12s, box-shadow .12s",
                 "&:hover": { borderColor: C.teal, boxShadow: "0 2px 12px rgba(16,24,40,.08)" },
                 "&:hover .open-hint": { opacity: 1 } }}>
        <Box sx={{ display: "flex", gap: 2, alignItems: "flex-start", justifyContent: "space-between" }}>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography sx={{ ...CARD_LEAD }}>{item.claim}</Typography>
            {item.subject_key && (
              <Typography sx={{ color: C.muted, fontSize: 12.5, lineHeight: 1.4, mt: 0.625 }}>
                {item.subject_key}
              </Typography>
            )}
          </Box>
          <TimeChip>{relTime(item.created_at)}</TimeChip>
        </Box>
        {/* placement — mirrors the Memory-log tree style (indented, one level per line) so the full
            path stays readable and the depth is visible, instead of a truncated one-liner */}
        <Box sx={{ mt: 1.125, pt: 1, borderTop: `1px dashed ${C.line}` }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.625 }}>
            <Typography sx={{ ...MICRO_LABEL, flex: 1 }}>Organized under</Typography>
            <Box component="span" className="open-hint" sx={{ flex: "none", fontSize: 11, fontWeight: 600,
                   color: C.tealDeep, opacity: 0, transition: "opacity .12s", whiteSpace: "nowrap" }}>
              Open in Memory log →
            </Box>
          </Box>
          {item.path && item.path.length ? (
            item.path.map((p, i) => (
              <Box key={p.id || i} sx={{ display: "flex", gap: 0.75, alignItems: "flex-start",
                     pl: `${i * 15}px`, py: "1.5px" }}>
                <Box component="span" sx={{ flex: "none", color: C.teal, fontSize: 11, mt: "2px" }}>▸</Box>
                <Typography sx={{ color: C.muted, fontSize: 12, lineHeight: 1.4,
                       display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical",
                       overflow: "hidden" }}>
                  {p.label}
                </Typography>
              </Box>
            ))
          ) : (
            <Typography sx={{ color: C.faint, fontSize: 12 }}>Top-level topic</Typography>
          )}
        </Box>
      </Box>
    </Box>
  );
}

export default function RecentCaptures({ items, loading, onOpen }) {
  if (loading) {
    return (
      <Box sx={{ height: "100%", display: "flex", alignItems: "center", justifyContent: "center" }}>
        <CircularProgress size={26} sx={{ color: C.teal }} />
      </Box>
    );
  }
  const has = items && items.length > 0;

  return (
    <Box sx={{ height: "100%", overflowY: "auto", bgcolor: C.canvas }}>
      <Box sx={{ maxWidth: 760, mx: "auto", px: { xs: 2, md: 3 }, py: 3 }}>
        <SectionHeader title="Recently captured"
                       subtitle="The latest things your agent learned · newest first" />

        {!has ? (
          <Box sx={{ textAlign: "center", py: 8, color: C.faint }}>
            <Typography sx={{ fontSize: 14 }}>
              Nothing captured yet. As you work in your assistant, new takeaways will show up here.
            </Typography>
          </Box>
        ) : (
          <Box sx={{ position: "relative" }}>
            {/* the timeline rail: a continuous line from the Live head down through the last node */}
            <Box sx={{ position: "absolute", left: "6px", top: "6px", bottom: "22px", width: "2px",
                   bgcolor: C.rail, borderRadius: "2px" }} />

            {/* Live head — the pulsing dot is the top of the rail */}
            <Box sx={{ position: "relative", pl: "30px", mb: 2, display: "flex", alignItems: "center",
                   minHeight: 14 }}>
              <Box component="span" sx={{ position: "absolute", left: "3px", top: "3px", width: 8, height: 8,
                     borderRadius: "50%", bgcolor: C.teal, boxShadow: `0 0 0 3px ${C.canvas}`, zIndex: 1,
                     animation: `${beat} 2.4s infinite`,
                     "@media (prefers-reduced-motion: reduce)": { animation: "none" } }} />
              <Typography sx={{ fontSize: 11.5, color: C.muted }}>Live — updates after each turn</Typography>
            </Box>

            {items.map((it) => <CaptureRow key={it.id} item={it} onOpen={onOpen} />)}
          </Box>
        )}
      </Box>
    </Box>
  );
}
